<template>
  <div
    v-if="showing == 'react'"
    id="code-snippet-react"
    class="code-snippet-tab"
    role="tabpanel"
    aria-labelledby="tab-react"
  >
    <div
      v-if="majorVersion !== 5"
      class="padding-top-sm padding-x-xl"
    >
      <ul
        class="code-snippet-secondary-tabs size-sm position-relative z-1"
        role="tablist"
        style="
          --list-style-type: none;
          --padding-left: 0;
          --margin-bottom: 0;
          --button-padding: var(--spacing-2xs) var(--spacing-sm);
          --button-border-radius: 0;
          --button-margin-bottom: 0;
        "
      >
        <li
          v-for="option in filteredOptions"
          :key="option.value"
          style="--display: inline-block"
          role="presentation"
        >
          <button
            :id="`tab-${option.value}`"
            class="link flat block code-snippet-secondary-tab"
            style="--button-active-background: transparent"
            type="button"
            role="tab"
            :aria-controls="`${option.value}`"
            :aria-selected="syntaxChoice === option.value ? 'true' : 'false'"
            @click="handleSnippetOption(option.value)"
          >
            {{ option.label }}
          </button>
        </li>
      </ul>
    </div>

    <wa-code-block
      add-highlighting
      allow-shadow-copy
      copy-text="Go to React Docs"
      copy-tracking-event="icon_react_code_copy_button_click"
      :copy-tracking-params="{ icon_name: icon.name, icon_style: styling.style, icon_family: styling.family }"
      custom-copy-icon="faClone"
      lang="jsx"
      :code="codeSnippet"
      @click.native="$emit('copyReact')"
    />

    <icon-code-snippet-links
      :has-custom-styling="hasCustomStyling"
      :links="reactLearnAboutLinks"
      :selected-style="styling.style"
      :major-version="majorVersion"
    />
  </div>
</template>

<script>
  import { camelCase, trimStart, upperFirst } from 'lodash'
  import { DocsMixin } from 'app/mixins'
  import { FAFamilyStylesThatAreExclusivelyProPlus } from '@fortawesome-internal-tools/fontawesome-icons/canonical' // eslint-disable-line import/no-unresolved
  import { getShortCode, isDuotoneFamily } from 'app/releases'
  import { transformIconColoring } from './utils.js'
  import IconCodeSnippetLinks from './IconCodeSnippetLinks'

  export default {
    name: 'IconCodeSnippetReact',

    components: {
      IconCodeSnippetLinks
    },

    mixins: [DocsMixin],

    props: {
      defaultStyles: {
        type: Object
      },
      hasCustomStyling: {
        type: Boolean
      },
      icon: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean
      },
      styling: {
        type: Object
      },
      translatedStyle: {
        required: true
      },
      showing: {
        required: false
      },
      majorVersion: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        syntaxChoice: 'kit_by_prefix_and_name'
      }
    },

    computed: {
      reactLearnAboutLinks() {
        const label = 'Adding Icons'

        let path = '/web/use-with/react/add-icons'
        return [{ label, path }]
      },

      filteredOptions() {
        return this.options.filter(
          (option) => !((this.isCustomIcon && option.value === 'svg_whole_style') || (this.isCustomIcon && option.value === 'svg_single'))
        )
      },

      options() {
        const defaults = [
          { label: 'Kit (byPrefixAndName)', value: 'kit_by_prefix_and_name' },
          { label: 'Kit (Single)', value: 'kit_single' }
        ]

        if (!this.proPlusFamilies.includes(this.styling?.family)) {
          defaults.push({ label: 'SVG (Whole Style)', value: 'svg_whole_style' }, { label: 'SVG (Single)', value: 'svg_single' })
        }

        defaults.push({ label: 'More...', value: 'more_options' })

        return defaults
      },

      translatedAnimation() {
        if (!this.styling || !this.styling.animation) {
          return ''
        } else if (this.styling.animation === 'beat-fade') {
          return ` beatFade`
        } else if (this.styling.animation === 'spin-reverse') {
          return ` spin spinReverse`
        } else if (this.styling.animation === 'spin-pulse') {
          return ` spinPulse`
        } else {
          return ` ${this.styling.animation}`
        }
      },

      translatedRotation() {
        if (!this.styling || !this.styling.rotation) {
          return ''
        } else if (this.styling.rotation.includes('rotate')) {
          let rotate = this.styling.rotation.split('-')
          return ` rotation={${rotate[1]}}`
        } else if (this.hasCustomRotation) {
          return ` rotation={${this.styling.rotation}}`
        } else if (this.styling.rotation.includes('flip')) {
          let flip = this.styling.rotation.split('-')

          return ` flip="${flip[1]}"`
        } else {
          return ''
        }
      },

      hasCustomRotation() {
        return Number.isInteger(parseInt(this.styling?.rotation))
      },

      translatedSize() {
        if (!this.styling || !this.styling.size) {
          return ''
        } else {
          return ` size="${this.styling.size}"`
        }
      },

      colorPrimary() {
        if (this.styling && isDuotoneFamily(this.styling.family) && this.styling.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` "--fa-primary-color": "${this.styling.colorPrimary}",`
        } else if (this.styling && this.styling.colorPrimary !== this.defaultStyles.colorPrimary) {
          return ` color: "${this.styling.colorPrimary}",`
        } else {
          return ''
        }
      },

      colorSecondary() {
        if (this.styling && !isDuotoneFamily(this.styling.family)) {
          return ''
        } else if (this.styling && this.styling.colorSecondary !== this.defaultStyles.colorSecondary) {
          return ` "--fa-secondary-color": "${this.styling.colorSecondary}",`
        } else {
          return ''
        }
      },

      opacityPrimary() {
        if (this.styling && !isDuotoneFamily(this.styling.family)) {
          return ''
        } else if (this.styling && this.styling.opacityPrimary !== this.defaultStyles.opacityPrimary) {
          return ` "--fa-primary-opacity": "${this.styling.opacityPrimary}",`
        } else {
          return ''
        }
      },

      opacitySecondary() {
        if (this.styling && !isDuotoneFamily(this.styling.family)) {
          return ''
        } else if (this.styling && this.styling.opacitySecondary !== this.defaultStyles.opacitySecondary) {
          return ` "--fa-secondary-opacity": "${this.styling.opacitySecondary}",`
        } else {
          return ''
        }
      },

      translatedColors() {
        const stringStyle = transformIconColoring(
          this.styling,
          this.defaultStyles,
          this.colorPrimary,
          this.colorSecondary,
          this.opacityPrimary,
          this.opacitySecondary
        )

        if (stringStyle) {
          return ` style={{${trimStart(stringStyle)}}}`
        } else {
          return ''
        }
      },

      iconPrefix() {
        return getShortCode(this.styling.family, this.styling.style)
      },

      codeSnippet() {
        const iconName = upperFirst(camelCase(this.icon.name))

        if (this.majorVersion === 5) {
          return `<FontAwesomeIcon icon={["${this.iconPrefix}", "${this.icon.name}"]}${this.translatedAnimation}${this.translatedRotation}${this.translatedSize}${this.translatedColors} />`
        }

        if (this.syntaxChoice === 'kit_by_prefix_and_name') {
          return `<FontAwesomeIcon icon={byPrefixAndName.${this.iconPrefix}['${this.icon.name}']}${this.translatedAnimation}${this.translatedRotation}${this.translatedSize}${this.translatedColors} />`
        }

        if (this.syntaxChoice === 'svg_whole_style') {
          return `<FontAwesomeIcon icon="${this.translatedStyle} fa-${this.icon.name}"${this.translatedAnimation}${this.translatedRotation}${this.translatedSize}${this.translatedColors} />`
        }

        if (this.syntaxChoice === 'svg_single' || this.syntaxChoice === 'kit_single') {
          return `<FontAwesomeIcon icon={fa${iconName}}${this.translatedAnimation}${this.translatedRotation}${this.translatedSize}${this.translatedColors} />`
        }

        return `Unsupported syntax choice '${this.syntaxChoice}' for version ${this.majorVersion}
                \nPlease contact support or check the console for details`
      },

      proPlusFamilies() {
        return [...FAFamilyStylesThatAreExclusivelyProPlus].map(([family]) => family)
      }
    },

    watch: {
      'styling.family'(newFamily) {
        // If the family is now a Pro+ family and user is on svg_single,
        // switch back to kit_by_prefix_and_name since individual imports aren't available for Pro+ families
        if (this.proPlusFamilies.includes(newFamily) && this.syntaxChoice === 'svg_single') {
          this.syntaxChoice = 'kit_by_prefix_and_name'
        }
      },

      'icon': {
        handler() {
          this.loadSavedPreference()
        },
        immediate: true
      },

      'filteredOptions': {
        handler() {
          this.loadSavedPreference()
        },
        deep: true
      }
    },

    mounted() {
      this.loadSavedPreference()
    },

    methods: {
      loadSavedPreference() {
        // Load saved React snippet option from localStorage
        const savedOption = localStorage.getItem('fa-code-snippet-option-react')
        const isSavedOptionValid = this.filteredOptions.some((option) => option.value === savedOption)

        if (savedOption && isSavedOptionValid) {
          // Only use saved preference if it's currently available
          this.syntaxChoice = savedOption
        } else {
          // Default to first available option if saved option is not available
          this.syntaxChoice = this.filteredOptions[0]?.value || 'kit_by_prefix_and_name'
        }
      },

      copyReact() {
        this.$clipboard(
          `<FontAwesomeIcon icon="${this.translatedStyle} fa-${this.icon.name}"${this.translatedAnimation}${this.translatedRotation}${this.translatedSize} ${
            this.translatedColors ? this.translatedColors : ''
          }/>`
        )
        this.$emit('markCopied', 'react')
      },

      handleSnippetOption(optionValue) {
        if (optionValue === 'more_options') {
          const link = this.reactLearnAboutLinks[0]
          const docsPath = this.getDocsPath(link, { version: this.majorVersion })
          const resolvedHref = this.$router.resolve(docsPath).href
          window.open(resolvedHref, '_blank')
          this.syntaxChoice = 'kit_by_prefix_and_name'
        } else {
          this.syntaxChoice = optionValue
        }

        // Save the user's preference to localStorage
        localStorage.setItem('fa-code-snippet-option-react', this.syntaxChoice)
      }
    }
  }
</script>
